import {
    Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
    InputAdornment, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography
} from "@mui/material"
import { DeleteOutlineOutlined, EditOutlined, Refresh, Search } from "@mui/icons-material"
import { FC, useCallback, useEffect, useState } from "react"
import _ from 'lodash'
import NoData from "../Common/NoData"
import { Apis } from "../../helpers"
import { TABLE_HEIGHT } from "../../configs"
import { EventsEmit } from "../../configs/events"

// The lock catalogue (collection "locks"): the lock models on offer, with prices in RMB. Not the TTLock devices - those are under
// Smart Locks. The server only lets the super admin and admins in, and deleting a lock also removes its image from DigitalOcean.
interface CatalogLock {
    _id: string
    page?: number
    model?: string
    name?: string
    variant?: string | null
    colors?: string[]
    price_rmb?: number | null
    fingerprint_version_extra_rmb?: number | null
    price_options_rmb?: Record<string, number> | null
    material?: string | null
    panel_size_mm?: string | null
    application?: string | null
    notes?: string | null
    image_url?: string
}

// what the edit form holds: everything as text
interface Form {
    name: string
    model: string
    variant: string
    colors: string
    material: string
    panel_size_mm: string
    price_rmb: string
    fingerprint_version_extra_rmb: string
    price_options: string
    application: string
    notes: string
}

interface EditState { lock: CatalogLock, form: Form, saving: boolean, error: string | null }
interface DeleteState { lock: CatalogLock, deleting: boolean, error: string | null }

const toForm = (lock: CatalogLock): Form => ({
    name: lock.name || '',
    model: lock.model || '',
    variant: lock.variant || '',
    colors: (lock.colors || []).join(', '),
    material: lock.material || '',
    panel_size_mm: lock.panel_size_mm || '',
    price_rmb: lock.price_rmb == null ? '' : String(lock.price_rmb),
    fingerprint_version_extra_rmb: lock.fingerprint_version_extra_rmb == null ? '' : String(lock.fingerprint_version_extra_rmb),
    price_options: Object.entries(lock.price_options_rmb || {}).map(([label, amount]) => `${label}: ${amount}`).join('\n'),
    application: lock.application || '',
    notes: lock.notes || ''
})

// "Label: price" per line -> { Label: price }; null if a line is not in that form
const parseOptions = (text: string): Record<string, number> | null => {
    const options: Record<string, number> = {}
    for (const line of text.split('\n').map(l => l.trim()).filter(Boolean)) {
        const cut = line.lastIndexOf(':')
        const amount = Number(line.slice(cut + 1).replace(/,/g, ''))
        if (cut < 1 || !line.slice(cut + 1).trim() || !Number.isFinite(amount)) return null
        options[line.slice(0, cut).trim()] = amount
    }
    return options
}

const priceText = (lock: CatalogLock) => {
    if (lock.price_rmb != null) return `RMB ${lock.price_rmb}`
    const options = Object.values(lock.price_options_rmb || {})
    return options.length ? `From RMB ${Math.min(...options)}` : '—'
}

const toast = (type: 'success' | 'error', message: string) => EventsEmit('modals', { action: 'toastMsg', type, message })

const AdminLocks: FC = () => {
    const [state, setState] = useState<{ locks: CatalogLock[], loading: boolean, refreshing: boolean, searchVal: string, edit: EditState | null, del: DeleteState | null }>({
        locks: [], loading: true, refreshing: false, searchVal: '', edit: null, del: null
    })
    const { locks, loading, refreshing, searchVal, edit, del } = state

    const fetchLocks = ({ search, processVariable }: { search?: string, processVariable?: string } = {}) => {
        const Search = search !== undefined ? search : state.searchVal
        if (processVariable) setState(s => ({ ...s, [processVariable]: true }))
        const afterLoad = { loading: false, refreshing: false, searchVal: Search }
        Apis({ module: 'Locks', action: 'getCatalogLocks', search: Search })
            .then((resp: any) => {
                if (resp.error) toast('error', resp.message)
                setState(s => resp.error ? { ...s, ...afterLoad } : { ...s, ...afterLoad, locks: resp.locks || [] })
            })
            .catch(() => setState(s => ({ ...s, ...afterLoad })))
    }

    // eslint-disable-next-line
    const startSearch = useCallback(_.debounce((val: string) => fetchLocks({ search: val, processVariable: 'loading' }), 500), [])

    useEffect(() => {
        fetchLocks({ processVariable: 'loading' })
        return () => startSearch.cancel()
        // eslint-disable-next-line
    }, [])

    // ---- edit ----
    const openEdit = (lock: CatalogLock) => setState(s => ({ ...s, edit: { lock, form: toForm(lock), saving: false, error: null } }))
    const closeEdit = () => setState(s => (s.edit?.saving ? s : { ...s, edit: null }))
    const changeForm = (patch: Partial<Form>) => setState(s => (s.edit ? { ...s, edit: { ...s.edit, form: { ...s.edit.form, ...patch }, error: null } } : s))

    const saveEdit = () => {
        if (!edit) return
        const { lock, form } = edit
        const options = parseOptions(form.price_options)
        if (options === null)
            return setState(s => (s.edit ? { ...s, edit: { ...s.edit, error: 'Price options: put one per line as "Version name: price".' } } : s))
        const sent = {
            name: form.name, model: form.model, variant: form.variant, material: form.material, panel_size_mm: form.panel_size_mm,
            application: form.application, notes: form.notes,
            colors: form.colors.split(',').map(c => c.trim()).filter(Boolean),
            price_rmb: form.price_rmb, fingerprint_version_extra_rmb: form.fingerprint_version_extra_rmb,
            price_options_rmb: Object.keys(options).length ? options : null
        }
        setState(s => (s.edit ? { ...s, edit: { ...s.edit, saving: true, error: null } } : s))
        Apis({ module: 'Locks', action: 'updateCatalogLock', catalogLockId: lock._id, lock: sent })
            .then((resp: any) => {
                if (resp.error)
                    return setState(s => (s.edit ? { ...s, edit: { ...s.edit, saving: false, error: resp.message } } : s))
                setState(s => ({ ...s, edit: null, locks: s.locks.map(l => l._id === lock._id ? resp.lock : l) }))
                toast('success', resp.message)
            })
            .catch(() => setState(s => (s.edit ? { ...s, edit: { ...s.edit, saving: false, error: 'Could not save. Please try again.' } } : s)))
    }

    // ---- delete ----
    const closeDelete = () => setState(s => (s.del?.deleting ? s : { ...s, del: null }))

    const confirmDelete = () => {
        if (!del) return
        const { lock } = del
        setState(s => (s.del ? { ...s, del: { ...s.del, deleting: true, error: null } } : s))
        Apis({ module: 'Locks', action: 'deleteCatalogLock', catalogLockId: lock._id })
            .then((resp: any) => {
                if (resp.error)
                    return setState(s => (s.del ? { ...s, del: { ...s.del, deleting: false, error: resp.message } } : s))
                setState(s => ({ ...s, del: null, locks: s.locks.filter(l => l._id !== lock._id) }))
                toast('success', resp.message)
            })
            .catch(() => setState(s => (s.del ? { ...s, del: { ...s.del, deleting: false, error: 'Could not delete. Please try again.' } } : s)))
    }

    const field = (label: string, key: keyof Form, extra: any = {}) => (
        <TextField
            label={label} size="small" fullWidth value={edit?.form[key] ?? ''} disabled={edit?.saving}
            onChange={e => changeForm({ [key]: e.target.value })} {...extra}
        />
    )

    return (
        <Box sx={{ pr: 5, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, mt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    <Typography sx={{ fontSize: 22, fontWeight: 'bold' }}>Locks</Typography>
                    {locks.length > 0 && <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{locks.length.toLocaleString()} models</Typography>}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                        size="small"
                        placeholder="Search name, model, material..."
                        onChange={e => startSearch(e.target.value)}
                        sx={{ width: 280, '& .MuiOutlinedInput-root': { fontSize: 13, borderRadius: 2 } }}
                        InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 17, color: 'text.disabled' }} /></InputAdornment> }}
                    />
                    <Tooltip title="Refresh">
                        <IconButton size="small" onClick={() => fetchLocks({ processVariable: 'refreshing' })} disabled={refreshing}>
                            {refreshing ? <CircularProgress size={18} /> : <Refresh sx={{ fontSize: 20 }} />}
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {!locks.length || loading ?
                <NoData message="No locks found." loading={loading} searchVal={searchVal} /> :
                <Paper sx={{ width: '100%' }} elevation={0} variant="outlined">
                    <TableContainer sx={{ maxHeight: TABLE_HEIGHT }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}></TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Lock</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Colours</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Price</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Fingerprint extra</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }}>Material</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: 13 }} align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {locks.map(lock => (
                                    <TableRow hover key={lock._id}>
                                        <TableCell>
                                            <Avatar variant="rounded" src={lock.image_url} alt={lock.name} sx={{ width: 48, height: 48 }}>{(lock.model || '?').slice(0, 2)}</Avatar>
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 13 }}>
                                            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{lock.name || '—'}</Typography>
                                            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                                                {lock.model}{lock.variant ? ` · ${lock.variant}` : ''}{lock.page ? ` · page ${lock.page}` : ''}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 13 }}>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {(lock.colors || []).map(color => <Chip key={color} size="small" variant="outlined" label={color} sx={{ fontSize: 11, height: 20 }} />)}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 13 }}>{priceText(lock)}</TableCell>
                                        <TableCell sx={{ fontSize: 13 }}>{lock.fingerprint_version_extra_rmb != null ? `+ RMB ${lock.fingerprint_version_extra_rmb}` : '—'}</TableCell>
                                        <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>{lock.material || '—'}</TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Edit">
                                                <IconButton size="small" onClick={() => openEdit(lock)}><EditOutlined sx={{ fontSize: 19 }} /></IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete the lock and its image">
                                                <IconButton size="small" onClick={() => setState(s => ({ ...s, del: { lock, deleting: false, error: null } }))}>
                                                    <DeleteOutlineOutlined sx={{ fontSize: 19, color: 'error.main' }} />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            }

            <Dialog open={Boolean(edit)} onClose={closeEdit} fullWidth maxWidth="sm">
                <DialogTitle sx={{ pb: 0.5 }}>
                    Edit lock
                    {edit && <Typography sx={{ fontSize: 13, color: 'text.secondary', fontWeight: 400 }}>{edit.lock.model}</Typography>}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                        {field('Name', 'name')}
                        <Box sx={{ display: 'flex', gap: 2 }}>{field('Model', 'model')}{field('Variant', 'variant')}</Box>
                        {field('Colours', 'colors', { helperText: 'Separate with commas, e.g. Black, Silver' })}
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            {field('Price (RMB)', 'price_rmb', { inputProps: { inputMode: 'decimal' } })}
                            {field('Fingerprint extra (RMB)', 'fingerprint_version_extra_rmb', { inputProps: { inputMode: 'decimal' } })}
                        </Box>
                        {field('Price options (RMB)', 'price_options', { multiline: true, minRows: 2, helperText: 'One per line as "Version name: price", when the price depends on the version. Names cannot contain a dot.' })}
                        <Box sx={{ display: 'flex', gap: 2 }}>{field('Material', 'material')}{field('Panel size (mm)', 'panel_size_mm')}</Box>
                        {field('Application', 'application', { multiline: true, minRows: 1 })}
                        {field('Notes', 'notes', { multiline: true, minRows: 1 })}
                        {edit?.error && <Typography sx={{ fontSize: 13, color: 'error.main' }}>{edit.error}</Typography>}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeEdit} disabled={edit?.saving} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="contained" onClick={saveEdit} disabled={edit?.saving || !edit?.form.name.trim() || !edit?.form.model.trim()}
                        startIcon={edit?.saving ? <CircularProgress size={16} color="inherit" /> : undefined} sx={{ textTransform: 'none' }}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(del)} onClose={closeDelete} fullWidth maxWidth="xs">
                <DialogTitle sx={{ pb: 0.5 }}>Delete {del?.lock.name || 'lock'}?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: 14, mt: 1 }}>
                        This deletes the lock and permanently removes its image from DigitalOcean. It cannot be undone.
                    </Typography>
                    {del?.error && <Typography sx={{ fontSize: 13, color: 'error.main', mt: 1.5 }}>{del.error}</Typography>}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeDelete} disabled={del?.deleting} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button
                        color="error" variant="contained" onClick={confirmDelete} disabled={del?.deleting}
                        startIcon={del?.deleting ? <CircularProgress size={16} color="inherit" /> : undefined} sx={{ textTransform: 'none' }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

export default AdminLocks
